import { Box } from '@chakra-ui/react';
import type { Msg, StickerCategory } from '@halo/chat-sdk-react';
import { isEmojiOnlyMessage, MessageEditStatus } from '@halo/chat-sdk-react';
import { MessageText, serializePayload } from '@halo/ui-chat-react';
import { useOpenLink } from '@/scripts/hooks';
import { useStaticAppStore } from '@/store/client';
import { useActionsStore } from '@/store/actions';

// Message
export interface MsgTextContentProps {
  message: Msg;
}

export const MsgTextContent = memo(function MsgTextContent({ message }: MsgTextContentProps) {
  const { t } = useTranslation();
  const { user, client } = useStaticAppStore();
  const { openLink } = useOpenLink();
  const fetchProfileUser = useActionsStore((store) => store.fetchProfileUser);

  const isEdited = useMemo(() => message?.edit === MessageEditStatus.Edited, [message?.edit]);

  const isEmojiOnly = useMemo(() => isEmojiOnlyMessage(message), [message]);

  const cssProps = useMemo(() => {
    if (isEmojiOnly) {
      return {
        display: 'flex',
        flexDirection: 'column',
      };
    }
  }, [isEmojiOnly]);

  const getStickerData = useCallback(
    async (id: string, category?: StickerCategory) => {
      return client?.Api.emoji.getStickerData({ sticker_id: id, category }).then((res) => {
        return res.render_data;
      });
    },
    [client]
  );

  const msgRef = useRef(message);
  msgRef.current = message;

  const text = useMemo(() => {
    const { payload, owner_id, at_read_list } = msgRef.current;
    return serializePayload.deserialize(
      payload,
      [
        {},
        {
          currentUserId: user?.user_id,
          senderId: owner_id,
          readerIds: at_read_list,
          onClick(user) {
            fetchProfileUser(user.user_id);
          },
        },
        {
          onOpen: openLink as any,
        },
      ],
      [
        {
          getData: getStickerData,
        },
        {},
        {},
      ]
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    message.payload.content,
    message.at_read_list.length,
    user?.user_id,
    openLink,
    getStickerData,
  ]);

  return (
    <MessageText
      fontSize="sm"
      {...cssProps}
      alignItems={isEmojiOnly ? 'var(--msg-item-align-items)' : undefined}
    >
      {text}
      {isEdited && (
        <Box as="span" fontSize="xs" color="fg.muted">
          ({t('chat.editedText')})
        </Box>
      )}
    </MessageText>
  );
});
